import { useEffect, useMemo, useState } from "react";
import { Save, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { adminApi, errText, fmtDate } from "./api";

export default function ContentEditor() {
  const [items, setItems] = useState(null);
  const [edits, setEdits] = useState({});
  const [page, setPage] = useState("home");
  const [saving, setSaving] = useState(false);

  const load = () => adminApi.get("/admin/content").then((r) => { setItems(r.data.items); setEdits({}); }).catch(() => toast.error("Could not load content."));
  useEffect(() => { load(); }, []);

  const pages = useMemo(() => (items ? [...new Set(items.map((i) => i.page))] : []), [items]);
  const dirtyCount = Object.keys(edits).length;

  const save = async () => {
    if (!dirtyCount) return;
    setSaving(true);
    try {
      const payload = Object.entries(edits).map(([key, value]) => ({ key, value }));
      await adminApi.put("/admin/content", { items: payload });
      toast.success(`Saved ${payload.length} change${payload.length > 1 ? "s" : ""}. The website is now updated.`);
      load();
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    if (dirtyCount && !window.confirm("Discard your unsaved changes?")) return;
    setEdits({});
  };

  if (!items) return <p className="admin-loading" data-testid="content-loading">Loading content…</p>;

  const visible = items.filter((i) => i.page === page);
  const sections = [...new Set(visible.map((i) => i.section))];

  return (
    <div data-testid="content-editor">
      <div className="admin-page-head">
        <h1>Content Editor</h1>
        <p>Edit the website copy. Saved changes go live on the public site immediately.</p>
      </div>
      <div className="admin-toolbar">
        <div className="filters">
          {pages.map((p) => (
            <button key={p} className={page === p ? "selected" : ""} onClick={() => setPage(p)} data-testid={`content-page-${p}`}>
              {p.charAt(0).toUpperCase() + p.slice(1)}
            </button>
          ))}
        </div>
        <div className="admin-actions">
          <button className="btn btn-secondary" onClick={reset} disabled={!dirtyCount} data-testid="content-reset-button"><Undo2 size={15} /> Reset</button>
          <button className="btn btn-primary" onClick={save} disabled={!dirtyCount || saving} data-testid="content-save-button">
            <Save size={15} /> {saving ? "Saving…" : `Save${dirtyCount ? ` (${dirtyCount})` : ""}`}
          </button>
        </div>
      </div>
      {sections.map((section) => (
        <section className="admin-panel" key={section}>
          <h2>{section}</h2>
          {visible.filter((i) => i.section === section).map((item) => {
            const value = edits[item.key] !== undefined ? edits[item.key] : item.value;
            const long = item.value.length > 80 || item.value.includes("\n");
            return (
              <label className="content-field" key={item.key}>
                <span className="content-label">
                  {item.label}
                  {item.updated_by && <em>Last updated {fmtDate(item.updated_at)} by {item.updated_by}</em>}
                </span>
                {long ? (
                  <textarea rows={3} value={value} onChange={(e) => setEdits({ ...edits, [item.key]: e.target.value })} data-testid={`content-input-${item.key}`} />
                ) : (
                  <input value={value} onChange={(e) => setEdits({ ...edits, [item.key]: e.target.value })} data-testid={`content-input-${item.key}`} />
                )}
              </label>
            );
          })}
        </section>
      ))}
    </div>
  );
}
